// ENG-9129: make manager startup observable, so a stalled boot cannot be silent.
//
// Why this exists — agt-aws-1, 2026-08-18:
//
//   08:18:27  ✔ Manager started (PID 560977, interval 10s)
//                    ...25 MINUTES, ZERO log lines...
//   08:43:22  [startup] rehydrated 16 agent state(s)          <- same PID
//   08:43:23  [manager] MCP assets deployed to ~/.augmented/_mcp
//
//   `deployMcpAssets()` runs after that rehydrate, so `_mcp` sat empty for 25
//   minutes while 16 agent containers were already up. Every one of them came
//   up with zero MCP tools bound; mcp-presence-reaper restart-looped them
//   (restarting cannot populate a mount) and the crash tally tripped the
//   breaker on the third try. 13 of 16 agents were auto-paused across five
//   teams. A crash-class trip never auto-resumes, so they stayed down until a
//   human intervened.
//
//   Nothing paged. Nothing logged. The process printed its own PID and then
//   went silent for 25 minutes, which is indistinguishable from a dead process
//   to anyone reading the log. It was found three hours later, by hand,
//   because someone happened to ask whether the incident was version-related.
//
// This module does two small things that would each have caught it:
//
//   1. A `[startup] begin` line emitted as the FIRST statement of
//      startManager(), before any work. This is not decoration — it BISECTS
//      the open question. Today the first log is the state rehydrate, so
//      "blocked before startManager()" and "blocked inside the state read"
//      produce byte-identical evidence: nothing. With a begin line, one of
//      them prints and the other does not, and the search space halves.
//
//   2. A slow-boot warning derived from `process.uptime()` at the moment the
//      poll loop starts. Uptime is measured from process spawn by the runtime
//      itself, so it captures the whole boot including everything that runs
//      before our first log statement — precisely the window that was invisible
//      here. Nothing has to be threaded through, and it cannot drift out of
//      sync with a manually captured timestamp.
//
// Design notes:
//
//   * PURE and side-effect free. No clock reads, no I/O — elapsed time is
//      passed in. That keeps it table-testable and keeps the house rule of
//      injecting time rather than mocking it (see restart-breaker.test.ts).
//   * The threshold is a WARNING boundary, never a failure. A slow boot on a
//      loaded host is not a fault and must never block a manager from starting;
//      this module only ever changes what gets logged.
//   * Deliberately no upper bound / no "too slow to continue" branch. The
//      manager recovering late is strictly better than not recovering.

/**
 * Boot durations at or above this are worth a human's attention.
 *
 * 60s is chosen to sit far above a healthy boot and far below a pathological
 * one, using the two measurements taken on agt-aws-1 on 2026-08-18:
 *   - healthy supervisor respawn: 11.3s  (10:17:44 -> 10:17:55)
 *   - the incident:            ~1,495s  (08:18:27 -> 08:43:22)
 * Anything in between is genuinely ambiguous and worth surfacing rather than
 * swallowing.
 */
export const SLOW_STARTUP_WARN_MS = 60_000;

/** Options for {@link shouldWarnSlowStartup}, so tests need not touch the constant. */
export interface SlowStartupOptions {
  /** Override the warn boundary. Defaults to {@link SLOW_STARTUP_WARN_MS}. */
  thresholdMs?: number;
}

/**
 * True when a boot took long enough to be worth flagging.
 *
 * Boundary is INCLUSIVE (`>=`): a boot exactly at the threshold warns. When the
 * question is "is this slow enough to look at", the tie should resolve toward
 * telling someone.
 *
 * Non-finite or negative input returns false. A clock that produced nonsense is
 * not evidence of a slow boot, and inventing a warning from a bad reading is
 * how a monitor starts asserting things it cannot substantiate.
 */
export function shouldWarnSlowStartup(
  elapsedMs: number,
  opts: SlowStartupOptions = {},
): boolean {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return false;
  const threshold = opts.thresholdMs ?? SLOW_STARTUP_WARN_MS;
  if (!Number.isFinite(threshold) || threshold < 0) return false;
  return elapsedMs >= threshold;
}

/**
 * Human-readable elapsed time for a log line: "11.3s", "1495.2s (24.9 min)".
 *
 * Minutes are appended past 60s because "1495.2s" alone does not read as
 * "twenty-five minutes" at a glance, and the whole point of the line is that a
 * human skimming a log notices it.
 */
export function formatStartupElapsed(elapsedMs: number): string {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return 'unknown';
  const seconds = elapsedMs / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${seconds.toFixed(1)}s (${(seconds / 60).toFixed(1)} min)`;
}

/**
 * The line the manager logs once the poll loop is up.
 *
 * Returns both the message and whether it is a warning, so the caller decides
 * how to emit it and the decision stays testable. The warning text names the
 * consequence rather than just the number: an operator reading it at 3am should
 * not have to already know the ENG-9129 story to understand why 25 minutes of
 * boot matters.
 */
export function describeStartupCompletion(
  elapsedMs: number,
  opts: SlowStartupOptions = {},
): { warn: boolean; message: string } {
  const elapsed = formatStartupElapsed(elapsedMs);
  if (!shouldWarnSlowStartup(elapsedMs, opts)) {
    return { warn: false, message: `[startup] ready in ${elapsed}` };
  }
  const threshold = opts.thresholdMs ?? SLOW_STARTUP_WARN_MS;
  return {
    warn: true,
    message:
      `[startup] SLOW BOOT — ready in ${elapsed}, over the ${formatStartupElapsed(threshold)} threshold. ` +
      'MCP bundles are deployed during startup, so agents spawning in this window can come up with no MCP ' +
      'tools (ENG-9129). Check for a build/install running on the host.',
  };
}

// ---------------------------------------------------------------------------
// ENG-9129 ask 4 — the window the `[startup] begin` line could not reach
// ---------------------------------------------------------------------------
//
// `[startup] begin` is the first statement of startManager(), so it bisects a
// stall into "before startManager" and "inside startManager". On agt-aws-1 the
// answer was "before", and the code says exactly where:
//
//   watchdog.ts, in-process path:
//     writePidFile(configDir, process.pid);              // pidfile exists
//     void import('./manager-worker.js')                 // <-- the gap
//       .then(({ startManager }) => startManager(...));
//     return { pid: process.pid };                       // banner prints
//
// startWatchdog RETURNS before the worker module is loaded. So the banner
// ("✔ Manager started (PID 560977)") and `[startup] worker pid=560977` really
// are the same process, exactly as the incident reported — and everything
// between them is one dynamic import of the largest module in the CLI.
//
// That is why the window was totally silent rather than merely sparse: `log()`
// is reached THROUGH the module being imported, so during the import there is
// no code loaded that could report on it. Nothing was suppressed; nothing was
// listening. deployMcpAssets() lives inside startManager() for the same
// reason, which is why `_mcp` carried an 08:43 mtime while containers had been
// up since 08:18.
//
// It also explains the contrast the incident could not: the 10:17 supervisor
// respawn took 11.3s on the same host because by then the bundle was in page
// cache and the provisioning writes had finished. A cold, contended read of a
// large bundle off a filesystem being rebuilt underneath it is the slow case.
//
// These helpers are pure for the same reasons as the ones above: elapsed time
// is passed in, so they stay table-testable and cannot drift from a clock.

/**
 * Loading the worker module for longer than this is worth a line a human will
 * stop on.
 *
 * Lower than {@link SLOW_STARTUP_WARN_MS} on purpose. That threshold covers a
 * whole boot, of which this import is one part; a 10s module load is already
 * far outside the healthy case (11.3s was the entire respawn, import included)
 * and is the earliest honest signal that the host's disk is the problem.
 */
export const SLOW_MODULE_LOAD_WARN_MS = 10_000;

/**
 * The line logged once the worker module has finished loading.
 *
 * Emitted from watchdog.ts, which reaches `log()` through the dependency-free
 * `manager/runtime.js` leaf rather than through the module it is timing — the
 * whole point is to say something during a window in which the heavy graph is
 * not available.
 */
export function describeWorkerModuleLoad(
  elapsedMs: number,
  opts: SlowStartupOptions = {},
): { warn: boolean; message: string } {
  const elapsed = formatStartupElapsed(elapsedMs);
  const threshold = opts.thresholdMs ?? SLOW_MODULE_LOAD_WARN_MS;
  if (!shouldWarnSlowStartup(elapsedMs, { thresholdMs: threshold })) {
    return { warn: false, message: `[startup] worker module loaded in ${elapsed}` };
  }
  return {
    warn: true,
    message:
      `[startup] SLOW MODULE LOAD — worker module took ${elapsed} to import, over the ` +
      `${formatStartupElapsed(threshold)} threshold. The manager had already written its pidfile and ` +
      'reported itself started, so it looked healthy for this whole window while doing nothing — and MCP ' +
      'bundles are deployed after this point, so agents spawning meanwhile come up with no MCP tools ' +
      '(ENG-9129). Check for a build, install or filesystem rebuild running on the host.',
  };
}

/**
 * Render a rejection value as something a human can act on.
 *
 * A rejected dynamic import does NOT guarantee an Error: a module that throws
 * a string or a plain object at evaluation time rejects with that value
 * verbatim. `String(value)` handles the string case and destroys the object
 * one — `{ code: 'ERR_MODULE_NOT_FOUND' }` becomes `[object Object]`, which is
 * strictly worse than saying nothing, because it looks like a real cause while
 * carrying none. Node itself attaches `code` to module-resolution failures, so
 * that object case is the realistic one, not a contrived one.
 *
 * JSON is used rather than a bespoke formatter so nested detail survives, and
 * it falls back to `String()` for the values JSON cannot represent (circular
 * references, BigInt) — those still read better as `[object Object]` than as a
 * thrown error inside the error reporter.
 */
function describeRejection(err: unknown): string {
  if (err instanceof Error) return err.stack ?? err.message;
  if (typeof err === 'object' && err !== null) {
    try {
      const json = JSON.stringify(err);
      // `undefined` (a function, a symbol) and `{}` (no own enumerable keys)
      // both mean JSON had nothing to show; neither is worth printing.
      if (json && json !== '{}') return json;
    } catch {
      // circular / BigInt — fall through
    }
  }
  // TOTAL by construction. `String(value)` is not safe on every value:
  // `String(Object.create(null))` throws `TypeError: Cannot convert object to
  // primitive value`, because a null-prototype object has no `toString` and no
  // `Symbol.toPrimitive`. That case reaches here rather than being caught
  // above, since `JSON.stringify` renders an EMPTY null-proto object as '{}',
  // which the branch above deliberately rejects as uninformative.
  //
  // This is not pedantry about an exotic input. The only caller is the
  // rejection handler for the worker-module import, and a throw there escapes
  // as an unhandled rejection — so the FATAL line never prints and the
  // `process.exit(1)` after it never runs. The process would hang exactly as
  // it did before this fix existed, which is the one outcome this code is here
  // to prevent. An error reporter that can fail is not a reporter.
  // (Found by CodeRabbit on PR #4731.)
  try {
    return String(err);
  } catch {
    return `<unprintable ${typeof err} rejection>`;
  }
}

/**
 * The line logged when the worker module import REJECTS.
 *
 * Before ENG-9129 this was `void import(...)` with no rejection handler: a
 * failed import left a process that had written a pidfile, printed "Manager
 * started", and would then never poll, never log, and never exit. `agt manager
 * status` reads the pidfile, so it would report a healthy manager indefinitely.
 * Saying so out loud is the minimum; the caller additionally exits non-zero so
 * a supervisor sees a failure instead of a hung child.
 */
export function describeWorkerModuleFailure(err: unknown): string {
  const detail = describeRejection(err);
  return (
    '[startup] FATAL — the manager worker module failed to load, so this process will never poll. ' +
    'The pidfile it already wrote would otherwise make it look like a healthy manager forever ' +
    `(ENG-9129). Exiting so the supervisor can react. Cause: ${detail}`
  );
}
